import type { QueryClient } from '@tanstack/react-query';

/**
 * The job billing rollup's query key, in one place.
 *
 * `GET /jobs/:id/billing` is derived server-side from the payment ledger and
 * the job's ACTIVE breakdown document (see backend/src/services/invoice-ledger.ts).
 * That means it moves for two different reasons — an invoice changed, or the
 * breakdown behind the contract value changed — and the mutations that cause
 * the second kind live nowhere near the ones that cause the first.
 *
 * Hence a standalone module rather than a const inside the hook that reads it:
 * useInvoices, useInvoicePayments and the breakdown hooks all need the key, and
 * routing it through any one of them would make them import each other.
 */
export const jobBillingKeys = {
  byJob: (jobId: string) => ['jobs', jobId, 'billing'] as const,
};

/**
 * Mark the job's billing rollup stale.
 *
 * REQUIRED after anything that changes an invoice or the active breakdown
 * document: the app sets a 5-minute global `staleTime` (App.tsx), so without an
 * explicit invalidation the Billing tab keeps serving the pre-edit figures even
 * across a tab switch or a re-navigation — which is NEW-242.
 */
export function invalidateJobBilling(qc: QueryClient, jobId: string) {
  return qc.invalidateQueries({ queryKey: jobBillingKeys.byJob(jobId) });
}
